/* sp-predictive-search.js — pesquisa preditiva do tema SP (header + /search).
   Reaproveita o PredictiveSearch do Dawn (assets/predictive-search.js): debounce, cache,
   live region e combobox ficam com ele. Aqui muda o que o Dawn tem cablado:
   - a seccao de resultados (sp-predictive-search, nao predictive-search) e os parametros
     da API (ate 6 produtos, sugestoes e coleccoes);
   - as opcoes: role="option" esta no proprio <a> (nao num <li> com link dentro), por isso
     as setas, o Enter e o submit procuram [role="option"];
   - os ids: a mesma seccao serve o header e a /search, portanto cada instancia prefixa os
     ids e os aria-labelledby com o id do seu contentor de resultados;
   - a altura maxima (so no painel do header): medida com a lista ja visivel, ate ao fundo
     do ecra visivel. Na /search o dropdown abre inteiro;
   - o botao de reset (o Dawn assume que existe; aqui nao ha);
   - Esc nunca apaga o termo (num input type=search o browser limpa-o); fecha a lista.
   Carregado a seguir ao predictive-search.js, so com settings.predictive_search_enabled. */
(() => {
  if (!window.customElements || typeof PredictiveSearch !== 'function') return;

  const OPTION = '[role="option"]';

  class SpPredictiveSearch extends PredictiveSearch {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId || 'sp-predictive-search';
      this.allPredictiveSearchInstances = document.querySelectorAll('sp-predictive-search');
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        // Pedido em curso: cancelar, senao a lista reabria sozinha depois do Esc.
        this.abortController.abort();
        this.abortController = new AbortController();
        this.close();
      });
      const refit = () => {
        if (this.isOpen) this.fitResults();
      };
      (window.visualViewport || window).addEventListener('resize', refit);
    }

    toggleResetButton() {
      if (this.resetButton) super.toggleResetButton();
    }

    onChange() {
      // Como o Dawn (que procura um id fixo, aqui prefixado): resultados que deixaram de
      // servir o termo novo saem ja, para nao reaparecerem ao reabrir.
      const next = this.getQuery();
      if (!this.searchTerm || !next.startsWith(this.searchTerm)) {
        this.querySelector('[data-sp-ps-listbox]')?.remove();
      }
      super.onChange();
    }

    onFormSubmit(event) {
      // Opcao escolhida pelas setas: o Enter abre-a (keyup -> selectOption), nao submete.
      if (this.querySelector(`${OPTION}[aria-selected="true"]`)) {
        event.preventDefault();
        return;
      }
      if (this.getQuery().length || this.hasAttribute('data-submit-empty')) return;
      event.preventDefault();
      this.input.focus();
    }

    getResultsMaxHeight() {
      const results = this.predictiveSearchResults;
      const vv = window.visualViewport;
      const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      // O painel do header tem padding por baixo da lista: tambem tem de caber no ecra.
      const panel = this.closest('.sp-hs__panel');
      const below = (panel ? parseFloat(getComputedStyle(panel).paddingBottom) || 0 : 0) + 12;
      return Math.max(120, Math.floor(bottom - results.getBoundingClientRect().top - below));
    }

    fitResults() {
      const style = this.predictiveSearchResults.style;
      // So o painel do header (sticky, nao rola com a pagina) se limita ao ecra. Na /search
      // o dropdown esta no fluxo da pagina: abre inteiro e alcanca-se com o scroll dela.
      if (!this.closest('.sp-hs__panel')) {
        style.removeProperty('max-height');
        return;
      }
      style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }

    open() {
      super.open();
      // O Dawn mede antes de pôr [open]; com a lista em display:none o top vinha a 0.
      this.fitResults();
    }

    renderSearchResults(resultsMarkup) {
      super.renderSearchResults(resultsMarkup);
      this.scopeIds();
    }

    scopeIds() {
      const results = this.predictiveSearchResults;
      const prefix = results.id;
      if (!prefix) return;
      results.querySelectorAll('[id]').forEach((el) => {
        el.id = `${prefix}-${el.id}`;
      });
      results.querySelectorAll('[aria-labelledby]').forEach((el) => {
        const ids = el.getAttribute('aria-labelledby').split(/\s+/).filter(Boolean);
        el.setAttribute('aria-labelledby', ids.map((id) => `${prefix}-${id}`).join(' '));
      });
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      const options = Array.from(this.querySelectorAll(OPTION)).filter((el) => el.offsetParent !== null);
      if (!options.length) return;
      const selected = this.querySelector(`${OPTION}[aria-selected="true"]`);
      const moveUp = direction === 'up';
      if (moveUp && !selected) return;

      const index = options.indexOf(selected);
      let next = 0;
      if (moveUp) next = index <= 0 ? options.length - 1 : index - 1;
      else if (selected) next = index === options.length - 1 ? 0 : index + 1;
      if (next === index) return;

      if (this.statusElement) this.statusElement.textContent = '';
      if (selected) selected.setAttribute('aria-selected', 'false');
      const active = options[next];
      active.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', active.id);
      if (!this.closest('.sp-hs__panel')) {
        // Na /search quem rola e a pagina: sem margem, a opcao subia para debaixo do
        // header sticky. A margem e o fundo do header, medido agora.
        const header = document.querySelector('header');
        const sticky = header && /sticky|fixed/.test(getComputedStyle(header).position);
        const top = sticky ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        active.style.scrollMarginTop = `${Math.ceil(top) + 8}px`;
      }
      active.scrollIntoView({ block: 'nearest' });
    }

    selectOption() {
      this.querySelector(`${OPTION}[aria-selected="true"]`)?.click();
    }

    dispatchSearchUpdateEvent(query) {
      const deferred = super.dispatchSearchUpdateEvent(query);
      // Um termo ultrapassado rejeita a promessa do evento; ninguem a apanha e a consola
      // mostrava "Uncaught (in promise)". Quem ouvir o evento continua a ver a rejeicao.
      deferred?.promise?.catch?.(() => {});
      return deferred;
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        return;
      }

      const params = new URLSearchParams({
        q: searchTerm,
        section_id: this.sectionId,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
      });

      fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) {
            this.close();
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector(`#shopify-section-${this.sectionId}`);
          if (!section) throw new Error('sp-predictive-search: section em falta na resposta');
          const resultsMarkup = section.innerHTML;
          this.allPredictiveSearchInstances.forEach((instance) => {
            if (instance.cachedResults) instance.cachedResults[queryKey] = resultsMarkup;
          });
          // O termo pode ter mudado enquanto o pedido corria: nao pintar resultados velhos.
          if (this.getQuery() !== searchTerm) return searchDeferred?.reject(new Error('stale'));
          this.renderSearchResults(resultsMarkup);
          searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        })
        .catch((error) => {
          searchDeferred?.reject(error);
          if (error?.name === 'AbortError' || error?.code === 20) return;
          this.close();
          console.error(error);
        });
    }
  }

  customElements.define('sp-predictive-search', SpPredictiveSearch);

  /* Header: a lupa e um <details>. Sem JS abre na mesma e o formulario GET funciona;
     com JS ganha foco no campo, Esc, botao fechar e clique fora. */
  document.querySelectorAll('details.sp-hs').forEach((details) => {
    const summary = details.querySelector('summary');
    const input = details.querySelector('input[type="search"]');
    const search = details.querySelector('sp-predictive-search');
    const shut = (focusSummary) => {
      details.open = false;
      if (focusSummary) summary.focus();
    };

    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', String(details.open));
      if (details.open) {
        input?.focus();
      } else {
        search?.close?.();
      }
    });
    details.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details.open) shut(true);
    });
    details.querySelector('.sp-hs__close')?.addEventListener('click', () => shut(true));
    document.addEventListener('click', (event) => {
      if (details.open && !details.contains(event.target)) shut(false);
    });
  });
})();
